import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance} from 'rsuite';
import PageContent from '@/components/PageContent';
import { BaseErrorModel, BaseResponseModel, LatLng, PageState } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import Textarea from '@/components/Textarea';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { getBoundingBoxCenter, getPolygonPathsFromGeoJson } from '@/utils/map';
import { hk_lat_lng } from '@/constants/map';
import { CityDistrict, UpsertCityDistrict } from '@/models/city-district.model';
import { upsertCityDistrict } from '@/services/city-district.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup, getCity } from '@/services/city.service';
import { City } from '@/models/city.model';

const CityDistrictSchema = Schema.Model<UpsertCityDistrict>({
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  jsonGeometry: Schema.Types.StringType().isRequired('This field is required.'),
});

interface CityDistrictFormProps{
  city?: City,
  formObject?: CityDistrict,
  pageState: PageState,
};
const emptyModel : UpsertCityDistrict = {
  cityId: undefined,
  name: '',
  jsonGeometry: undefined,
};
const CityDistrictForm: React.FC<CityDistrictFormProps> = ({city, formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertCityDistrict>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [polygonPaths, setPolygonPaths] = useState<LatLng[]>([]);
  const [mapCenter, setMapCenter] = useState<LatLng>();
 const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, cityId: city?.id});
      if(city){
        setMapCenter({lat: city?.lat, lng: city?.lon});
      }
      return;
    }

    if (formObject) {
      setFormValue(formObject);
      updatePolygon(formObject.jsonGeometry);
    } else {
      setFormValue(emptyModel);
    }
  },[formObject, city]);

  const handleSubmit = async () => {
    // console.log(formValue);
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<CityDistrict> = await upsertCityDistrict(formValue);
      if (result.success) {
        toastSuccessMessage('City District saved successfully.');
         nav(`/cities/${formValue.cityId}`, {state: {tab: 'city-districts'}});
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };
  
  const updateSelectedCity = async(cityId: number|undefined) => {
    if(cityId){
      getCity(cityId).then(result => {
        setMapCenter({lat: result.result?.lat, lng: result.result?.lon});
      });
      return;
    }
  };

  const updatePolygon = (geoJson: string) => {
    const polygons = getPolygonPathsFromGeoJson(geoJson);
    if(polygons && polygons.length > 0) {
      setPolygonPaths(polygons);
      setMapCenter(getBoundingBoxCenter(polygons[0]));
      return true;
    } else { 
      setPolygonPaths([]);
      setMapCenter(undefined);
      return false;
    }
  };

  const handleJsonChangeChange = (geoJson: string) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      jsonGeometry: geoJson
    }));
    updatePolygon(geoJson);
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };
  return (
    <>
      <PageContent>
        <Form fluid ref={formRef}
          onCheck={setFormError}
          formValue={formValue}
          onChange={handleFormChange}
          model={CityDistrictSchema}
          disabled={loading}
        >
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:8}} >
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control block
                    name= "cityId" 
                    accepter= {FetchingSelect} 
                    fetchData= {(text, page) => getCitiesLookup(text, undefined, page)}
                    disabled= {!!city && pageState == PageState.New} 
                    onChange={ val => updateSelectedCity(val)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Name </Form.Label>
                  <Form.Control name="name"/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Json Geometry</Form.Label>
                  <Form.Control 
                    name="jsonGeometry" 
                    value={formValue.jsonGeometry ?? ''} 
                    accepter={Textarea} 
                    style={{height: '550px'}}
                    onChange={handleJsonChangeChange}
                  />
                </Form.Group>
              </Col>
              <Col span={{xs:24, xl:16}} style={{textAlign: 'center'}}>
                <Form.Group>
                  <Form.Label>Geometry On Map</Form.Label>
                    {isLoaded ? <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '700px', borderRadius: 10 }}
                      center={mapCenter || hk_lat_lng}
                      zoom={12}
                    >
                      <Polygon
                        paths={polygonPaths}
                        options={{
                          fillColor: '#2196f3',
                          fillOpacity: 0.4,
                          strokeColor: '#2196f3',
                          strokeOpacity: 1,
                          strokeWeight: 2
                        }}
                      />
                    </GoogleMap> : '' }                
                </Form.Group>
              </Col>              
            </Row>
          </Grid>
        </Form>

        <Divider />
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

        <Stack justifyContent="space-between">
          <IconButton icon={<PagePreviousIcon />} onClick={() => nav(`/cities/${formObject?.cityId}`, {state:{tab:'city-districts'}})}>
            Back
          </IconButton>
          <IconButton
            icon={<PageNextIcon />}
            placement="right"
            appearance="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            Save
          </IconButton>
        </Stack>
      </PageContent>
    </>
  );
};

export default CityDistrictForm;
